export enum EPartnerCodeType {
  unfunded = 'unfunded',
  funded = 'funded',
}

export const EPartnerCodeTypeLabels = {
  [EPartnerCodeType.unfunded]: 'Partner Code',
  [EPartnerCodeType.funded]: 'Partner-Funded',
}

export const EPartnerCodeTypeOptions = Object.values(EPartnerCodeType).map((codeType) => ({
  value: codeType,
  label: EPartnerCodeTypeLabels[codeType],
}))

export enum EPartnerCodeStatus {
  active = 'active',
  inactive = 'inactive',
}

export const EPartnerCodeStatusLabels = {
  [EPartnerCodeStatus.active]: 'Active',
  [EPartnerCodeStatus.inactive]: 'Inactive',
}

export const EPartnerCodeStatusOptions = Object.values(EPartnerCodeStatus).map((status) => ({
  value: status,
  label: EPartnerCodeStatusLabels[status],
}))

/**
 * Every partner-funded license is an annual Family plan — the backend sends the duration as
 * read-only metadata and there is no way to configure it on a code.
 */
export type PartnerCodeLicenseDuration = '1_year'

export const PARTNER_CODE_LICENSE_DURATION_LABELS: Record<PartnerCodeLicenseDuration, string> = {
  '1_year': '1 year',
}

/** Seats granted by a partner-funded activation. Paid Family plans have their own, smaller limit. */
export const PARTNER_FUNDED_FAMILY_SEATS = 10

export interface PartnerCodeContactFields {
  contactFirstName?: string | null
  contactLastName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  note?: string | null
}

export interface PartnerCode extends PartnerCodeContactFields {
  id: number
  codeType: EPartnerCodeType
  code: string
  organizationName: string
  /** License cap of a funded code. Unfunded codes have no user limit and carry null here. */
  numberOfUsers: number | null
  timesUsed: number
  /**
   * Same values as numberOfUsers / timesUsed, named the way the spec names the columns. The cap and
   * the remainder are null for unfunded codes; usage is counted for both types.
   */
  totalLicenses: number | null
  licensesUsed: number
  licensesRemaining: number | null
  status: EPartnerCodeStatus
  /** Set for funded codes only; unfunded codes grant no license and carry no duration. */
  licenseDuration: PartnerCodeLicenseDuration | null
  createdAt: string
}

export interface CreatePartnerCodeRequest extends PartnerCodeContactFields {
  codeType: EPartnerCodeType
  organizationName: string
  /** Required for funded codes (a positive integer); unfunded codes have no limit. */
  numberOfUsers?: number | null
}

// Status and contacts are editable; code, type, organization and license count are immutable.
export interface UpdatePartnerCodeRequest extends PartnerCodeContactFields {
  id: number
  status?: EPartnerCodeStatus
}

/**
 * How the backend classified an entered code. It answers 200 in every case — including an
 * exhausted funded code — and the caller routes on the outcome:
 *
 * - `funded_available` — activate a partner-funded subscription, no payment;
 * - `unfunded` / `funded_exhausted` — continue to the paid flow;
 * - `invalid` / `inactive` — stay on the code input and show `message`.
 */
export type PartnerCodeValidationOutcome =
  | 'invalid'
  | 'inactive'
  | 'funded_available'
  | 'unfunded'
  | 'funded_exhausted'

export interface ValidatePartnerCodeRequest {
  code: string
}

export interface ValidatePartnerCodeResponse {
  outcome: PartnerCodeValidationOutcome
  /** User-facing copy owned by the backend — show it as is instead of rewording per outcome. */
  message: string
}
